/** The asset page's Metadata tab: the model's form, the incomplete-metadata warning and archived values. */
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Save } from 'lucide-react';
import { missingNeededFields, rightsFields, validateMetadata, type AssetDetail, type MetadataSchemaDefinition } from '@prism/contracts';
import type { ApiError } from '../api/client';
import { useMe, useRightsSettings, useSaveMetadata, useSchema } from '../api/hooks';
import { can } from '../lib/permissions';
import { MetadataSections } from '../components/MetadataFields';
import { toast } from '../components/Toasts';
import { AssetGroupsRow, InheritedFieldValue } from './groups/AssetGroups';
// 1.6.0 (A, metadata/MetadataSuggestions.tsx): metadata suggestions to accept or reject.
import { MetadataSuggestions } from './metadata/MetadataSuggestions';

/**
 * `only` (1.8.0, the asset's Rights tab): the form of just these fields, without the suggestions, the groups and the
 * archived values. While rights management is on, rights fields are read-only without rights.manage (the server
 * refuses the change: RIGHTS_MANAGE_REQUIRED).
 */
export function MetadataTab({ asset, only }: { asset: AssetDetail; only?: readonly string[] }) {
  const schemaQ = useSchema();
  const me = useMe().data!;
  const rightsOn = Boolean(useRightsSettings().data?.enabled);
  const save = useSaveMetadata(asset.id);
  const [values, setValues] = useState<Record<string, unknown>>(asset.metadata);
  const [base, setBase] = useState({ revision: asset.metadataRevision, values: asset.metadata });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState<string | null>(null);
  const canEdit = me.permissions.includes('asset.edit_metadata');
  const fullSchema = schemaQ.data?.definition;
  const schema = useMemo(() => (fullSchema && only ? narrowSchema(fullSchema, only) : fullSchema), [fullSchema, only]);
  const editable = useMemo(() => {
    const base = schemaQ.data?.editable;
    if (!rightsOn || !fullSchema || can(me, 'rights.manage')) return base;
    const locked = Object.values(rightsFields(fullSchema)).map((f) => [f!.id, false] as const);
    return locked.length ? { ...(base ?? {}), ...Object.fromEntries(locked) } : base;
  }, [schemaQ.data?.editable, rightsOn, fullSchema, me]);

  // Adopt server changes when we have no local edits.
  const dirtyKeys = useMemo(
    () => Object.keys({ ...values, ...base.values }).filter((k) => JSON.stringify(values[k] ?? null) !== JSON.stringify(base.values[k] ?? null)),
    [values, base],
  );
  useEffect(() => {
    if (asset.metadataRevision !== base.revision && dirtyKeys.length === 0) {
      setValues(asset.metadata);
      setBase({ revision: asset.metadataRevision, values: asset.metadata });
    }
  }, [asset.metadataRevision, asset.metadata, base.revision, dirtyKeys.length]);

  if (!schema) return <div className="skeleton" style={{ height: 200 }} />;
  // What the form would leave empty, live as the person types: the same rule the server uses for the saved flag.
  const missingFields = missingNeededFields(schema, values, asset.category?.id ?? null);

  const set = (id: string, v: unknown) => {
    setValues((prev) => ({ ...prev, [id]: v }));
    setErrors((e) => { const n = { ...e }; delete n[id]; return n; });
  };

  async function submit() {
    const local = validateMetadata(fullSchema!, values, asset.category?.id ?? null);
    if (!local.ok) { setErrors(local.errors); return; }
    const changed = Object.fromEntries(dirtyKeys.map((k) => [k, values[k] ?? null]));
    try {
      const updated = await save.mutateAsync({ revision: base.revision, values: changed });
      setBase({ revision: updated.metadataRevision, values: updated.metadata });
      setValues(updated.metadata);
      setConflict(null);
      toast('Metadata saved');
    } catch (err) {
      const e = err as ApiError;
      if (e.code === 'VALIDATION_FAILED') setErrors((e.details?.fields as Record<string, string>) ?? {});
      else if (e.code === 'REVISION_CONFLICT') setConflict(e.message);
      else toast(e.message, 'error');
    }
  }

  function reloadServer() {
    setValues(asset.metadata);
    setBase({ revision: asset.metadataRevision, values: asset.metadata });
    setConflict(null);
  }

  return (
    <form className="form" onSubmit={(e) => { e.preventDefault(); void submit(); }} data-testid="metadata-form"
      onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); void submit(); } }}>
      {!canEdit && <div className="banner info">Your role can view metadata but not change it.</div>}
      {!only && <MetadataSuggestions asset={asset} canEdit={canEdit} />}
      {conflict && (
        <div className="banner warning" role="alert"><div className="banner-body"><strong>Edited elsewhere</strong><span>{conflict}</span>
          <div className="actions"><button type="button" className="btn sm" onClick={reloadServer}>Load latest</button></div></div></div>
      )}
      {missingFields.length > 0 && (
        <div className="banner warning" role="status" data-testid="incomplete-metadata"><div className="banner-body">
          <strong><AlertTriangle size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />Incomplete metadata</strong>
          <span>These fields are needed and still empty: {missingFields.map((f, i) => (
            <span key={f.id}>{i > 0 && ', '}<button type="button" className="link-btn" onClick={() => document.querySelector<HTMLElement>(`[data-field="${f.id}"]`)?.scrollIntoView({ block: 'center' })}>{f.label}</button></span>
          ))}. You can save without them.</span>
        </div></div>
      )}
      {/* 1.4.0 (G2): the asset's groups, and inherited values in grey under empty fields. */}
      {!only && <AssetGroupsRow asset={asset} place="metadata" />}
      <MetadataSections schema={schema} values={values} categoryId={asset.category?.id ?? null} errors={errors} onChange={set} canEdit={canEdit} editable={editable}
        renderAfterField={(f, v) => <InheritedFieldValue asset={asset} field={f} value={v} />} />
      {!only && Object.keys(asset.metadataArchive).length > 0 && (
        <div className="form-section" data-testid="metadata-archive">
          <p className="section-label" style={{ margin: 0 }}>Archived values</p>
          <span className="help">Kept when the metadata model changed. They are no longer searchable, but they are never deleted.</span>
          <div className="archive-list">
            {Object.entries(asset.metadataArchive).map(([id, a]) => (
              <div className="archive-item" key={id}>
                <strong>{a.label}</strong><span className="faint">from model v{a.fromVersion}</span>
                <span className="mono">{Array.isArray(a.value) ? a.value.join(', ') : String(a.value)}</span><span className="muted">{a.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {canEdit && (
        <div className="form-footer">
          <button className="btn primary" type="submit" disabled={!dirtyKeys.length || save.isPending} data-testid="save-metadata">
            {save.isPending ? <Loader2 size={14} className="spin" /> : <Save size={14} />} Save
          </button>
          <button className="btn ghost" type="button" disabled={!dirtyKeys.length} onClick={() => { setValues(base.values); setErrors({}); }}>Discard</button>
          <span className="faint" style={{ fontSize: 12 }}>{dirtyKeys.length ? `${dirtyKeys.length} unsaved change${dirtyKeys.length > 1 ? 's' : ''} · ⌘S` : `Revision ${base.revision}`}</span>
        </div>
      )}
    </form>
  );
}

/** The model with only these fields, in one tab (the Rights tab's form). */
function narrowSchema(def: MetadataSchemaDefinition, ids: readonly string[]): MetadataSchemaDefinition {
  const fields = def.fields.filter((f) => ids.includes(f.id));
  const used = new Set(fields.map((f) => f.section ?? def.sections[0]?.id));
  return { ...def, tabs: undefined, fields, sections: def.sections.filter((s) => used.has(s.id)).map((s) => ({ id: s.id, label: s.label })) };
}

